<!--
Copyright (C) 2024 Checkmk GmbH - License: GNU General Public License v2
This file is part of Checkmk (https://checkmk.com). It is subject to the terms and
conditions defined in the file COPYING, which is part of this source code package.
-->
<script setup lang="ts">
import { ref, watch } from 'vue'

import usei18n from '@/lib/i18n'
import type { TranslatedString } from '@/lib/i18nString'
import usePersistentRef from '@/lib/usePersistentRef'

import CmkAlertBox from '@/components/CmkAlertBox.vue'
import CmkButton from '@/components/CmkButton.vue'
import CmkCode from '@/components/CmkCode.vue'
import CmkDialog from '@/components/CmkDialog.vue'
import CmkIcon from '@/components/CmkIcon'
import CmkLinkCard from '@/components/CmkLinkCard'
import CmkTabs, { CmkTab, CmkTabContent } from '@/components/CmkTabs'
import CmkToggleButtonGroup from '@/components/CmkToggleButtonGroup.vue'
import CmkWizard, { CmkWizardButton, CmkWizardStep } from '@/components/CmkWizard'
import CmkHeading from '@/components/typography/CmkHeading.vue'
import CmkParagraph from '@/components/typography/CmkParagraph.vue'

import type { AgentSlideOutTabs } from '../lib/type_def'
import GenerateToken from './GenerateToken.vue'
import RegisterAgent from './steps/RegisterAgent.vue'

const props = defineProps<{
  dialogMsg: TranslatedString
  tabs: AgentSlideOutTabs[]
  allAgentsUrl: string
  userSettingsUrl: string
  closeButtonTitle: TranslatedString
  saveHost: boolean
  hostExists?: boolean
  setupError?: boolean
  agentInstalled: boolean
  isPushMode: boolean
  hostName: string
  agentReceiverPortIsDefault: boolean
  canDownloadBakedAgents: boolean
}>()

const { _t } = usei18n()

const emit = defineEmits(['close'])
const close = () => {
  emit('close')
}

const openedTab = ref<string>(sessionStorage.getItem('slideInTabState') || 'linux')

const openAllAgentsPage = (url: string) => {
  window.open(url, '_blank')
}

const packageFormatRpm = 'rpm'
const packageFormatDeb = 'deb'
const packageFormatTgz = 'tgz'

const model = ref(sessionStorage.getItem('slideInModelState') || packageFormatDeb)
sessionStorage.removeItem('slideInModelState')
sessionStorage.removeItem('slideInTabState')

const selectedVariantId = usePersistentRef<string>('slideInSelectedVariantId', 'powershell', (v) =>
  typeof v === 'string' ? v : 'powershell'
)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const cmk: any
function saveHostAction() {
  sessionStorage.setItem('reopenSlideIn', 'true')
  sessionStorage.setItem('slideInModelState', model.value)
  sessionStorage.setItem('slideInTabState', openedTab.value)
  sessionStorage.setItem('slideInAgentInstalled', String(props.agentInstalled))
  cmk.page_menu.form_submit('edit_host', 'save_and_edit')
}
const ott = ref<string | null | Error>(null)
watch([openedTab, model], () => {
  ott.value = null
})

function getStatusCmd(tab: AgentSlideOutTabs): string {
  const variants = tab.statusCmdVariants
  if (variants && variants.length > 0) {
    return variants.find((v) => v.id === selectedVariantId.value)?.cmd ?? variants[0]!.cmd
  }
  return tab.statusCmd
}

function tabNeedsToken(tab: AgentSlideOutTabs): boolean {
  if (tab.installCmd) {
    return true
  }
  if (tab.toggleButtonOptions) {
    return !!(
      (model.value === packageFormatDeb && tab.installDebCmd) ||
      (model.value === packageFormatRpm && tab.installRpmCmd) ||
      (model.value === packageFormatTgz && tab.installTgzCmd)
    )
  }
  return false
}

function installCmdWithToken(cmd: string | undefined): string {
  if (!cmd) {
    return ''
  }
  if (ott.value && !(ott.value instanceof Error)) {
    return cmd.replace('[AGENT_DOWNLOAD_OTT]', ott.value)
  }
  return cmd
}

const currentStep = ref(getInitStep())
function getInitStep() {
  if (props.saveHost) {
    return 1
  }
  if (!props.agentInstalled) {
    return 2
  }
  return 3
}
</script>

<template>
  <CmkButton :title="closeButtonTitle" class="close_and_test" @click="close">
    <CmkIcon name="connection-tests" />
    {{ closeButtonTitle }}
  </CmkButton>
  <CmkButton
    :title="hostExists ? _t('View host agents') : _t('View all agents')"
    class="all_agents"
    @click="() => openAllAgentsPage(allAgentsUrl)"
  >
    <CmkIcon name="frameurl" />
    {{ hostExists ? _t('View host agents') : _t('View all agents') }}
  </CmkButton>
  <CmkDialog
    :message="dialogMsg"
    :dismissal_button="{ title: _t('Do not show again'), key: 'agent_slideout' }"
  />
  <CmkHeading type="h4" class="select-heading">
    {{ _t('Select the type of system you want to monitor') }}
  </CmkHeading>
  <CmkTabs v-model="openedTab">
    <template #tabs>
      <CmkTab v-for="tab in tabs" :id="tab.id" :key="tab.id" class="tabs">
        <h2>{{ tab.title }}</h2>
      </CmkTab>
    </template>
    <template #tab-contents>
      <CmkTabContent v-for="tab in tabs" :id="tab.id" :key="tab.id">
        <CmkToggleButtonGroup
          v-if="tab.toggleButtonOptions"
          v-model="model"
          :options="tab.toggleButtonOptions"
        />
        <CmkWizard v-model="currentStep" mode="guided">
          <CmkWizardStep :index="1" :is-completed="() => currentStep > 1 || !saveHost">
            <template #header>
              <CmkHeading> {{ _t('Save host') }}</CmkHeading>
            </template>

            <template #content>
              <div class="save_host__div">
                <CmkParagraph>
                  {{
                    _t(
                      'Agent registration is only possible for hosts that already exist in Checkmk (they don’t need to be activated yet).'
                    )
                  }}
                </CmkParagraph>
              </div>
              <div v-if="setupError" class="save_host__div">
                <CmkParagraph class="agent_slideout__paragraph_host_exists">
                  <CmkIcon name="cross" />
                  {{
                    _t(
                      `Could not save host "${hostName}". Close the slideout and review your input.`
                    )
                  }}
                </CmkParagraph>
              </div>
              <div v-else-if="!saveHost && hostExists" class="save_host__div">
                <CmkParagraph class="agent_slideout__paragraph_host_exists">
                  <CmkIcon name="checkmark" />
                  {{ _t(`Host "${hostName}" exists`) }}
                </CmkParagraph>
              </div>
            </template>
            <template #actions>
              <CmkButton v-if="setupError" :title="_t('Close and revise form')" @click="close">
                <CmkIcon name="edit" />
                {{ _t('Close & review') }}
              </CmkButton>
              <CmkWizardButton
                v-if="saveHost && !setupError"
                :override-label="_t('Save host & next step')"
                type="next"
                @click="saveHostAction"
              />
              <CmkWizardButton v-else-if="currentStep === 1 && !setupError" type="next" />
            </template>
          </CmkWizardStep>

          <CmkWizardStep :index="2" :is-completed="() => currentStep > 2 || agentInstalled">
            <template #header>
              <CmkHeading> {{ _t('Download and install') }}</CmkHeading>
            </template>
            <template #content>
              <div v-if="currentStep === 2" class="download_install__content">
                <template v-if="tabNeedsToken(tab) && canDownloadBakedAgents">
                  <div class="download_install__token">
                    <CmkParagraph>{{ tab.installMsg }}</CmkParagraph>
                    <GenerateToken
                      v-model="ott"
                      token-generation-endpoint-uri="domain-types/agent_download_token/collections/all"
                      :description="
                        _t(
                          'To securely fetch the latest agent package via the command line, you must first generate a temporary authentication token. ' +
                            'This token is valid for 7 days and is used solely for the download process'
                        )
                      "
                      :expires-in-seconds="604800"
                      :token-generation-body="{}"
                    />
                  </div>
                  <template v-if="ott !== null">
                    <template v-if="tab.installCmdVariants && tab.installCmdVariants.length > 1">
                      <CmkToggleButtonGroup
                        v-model="selectedVariantId"
                        class="shell-toggle"
                        :options="
                          tab.installCmdVariants.map((v) => ({ label: v.label, value: v.id }))
                        "
                      />
                      <template v-for="variant in tab.installCmdVariants" :key="variant.id">
                        <template v-if="variant.id === selectedVariantId">
                          <CmkCode
                            :title="_t('Download the agent')"
                            :code_txt="installCmdWithToken(variant.downloadCmd || '')"
                            class="code"
                            width="fill"
                          />
                          <CmkCode
                            :title="_t('Install the agent')"
                            :code_txt="variant.installCmd"
                            class="code"
                            width="fill"
                          />
                        </template>
                      </template>
                    </template>
                    <template v-else-if="tab.installDownloadCmd">
                      <CmkCode
                        :title="_t('Download agent')"
                        :code_txt="installCmdWithToken(tab.installDownloadCmd)"
                        class="code"
                        width="fill"
                      />
                      <CmkCode
                        :title="_t('Install agent')"
                        :code_txt="tab.installCmd || ''"
                        class="code"
                        width="fill"
                      />
                    </template>
                    <CmkCode
                      v-else-if="tab.installMsg && tab.installCmd"
                      :code_txt="installCmdWithToken(tab.installCmd)"
                      class="code"
                      width="fill"
                    />
                    <CmkCode
                      v-if="tab.installMsg && tab.installDebCmd && model === packageFormatDeb"
                      :code_txt="installCmdWithToken(tab.installDebCmd)"
                      class="code"
                      width="fill"
                    />
                    <CmkCode
                      v-if="tab.installMsg && tab.installRpmCmd && model === packageFormatRpm"
                      :code_txt="installCmdWithToken(tab.installRpmCmd)"
                      class="code"
                      width="fill"
                    />
                    <CmkCode
                      v-if="tab.installMsg && tab.installTgzCmd && model === packageFormatTgz"
                      :code_txt="installCmdWithToken(tab.installTgzCmd)"
                      class="code"
                      width="fill"
                    />
                  </template>
                </template>
                <div v-else-if="tab.installUrl" class="install_url__div">
                  <CmkParagraph v-if="tab.installUrl.msg">{{ tab.installUrl.msg }}</CmkParagraph>
                  <CmkLinkCard
                    :title="tab.installUrl.title"
                    :url="tab.installUrl.url"
                    :icon-name="tab.installUrl.icon"
                    :open-in-new-tab="true"
                  />
                </div>
                <CmkAlertBox
                  v-else-if="tabNeedsToken(tab) && !canDownloadBakedAgents"
                  variant="warning"
                >
                  {{
                    _t(
                      'You do not have the required permissions to download monitoring agents. ' +
                        'Please ask your administrator to grant you the required permissions, or ask them to install the agent on this host.'
                    )
                  }}
                </CmkAlertBox>
              </div>
            </template>
            <template v-if="currentStep === 2" #actions>
              <CmkWizardButton
                type="next"
                :disabled="
                  tabNeedsToken(tab) &&
                  (ott === null || ott instanceof Error || !canDownloadBakedAgents)
                "
                :override-label="_t('Next step: Register agent')"
              />
              <CmkWizardButton type="previous" />
            </template>
          </CmkWizardStep>

          <RegisterAgent
            v-model:selected-variant-id="selectedVariantId"
            :index="3"
            :is-completed="() => currentStep > 3 || !tab.registrationMsg"
            :tab="tab"
            :is-push-mode="isPushMode"
            :close-button-title="closeButtonTitle"
            :host-name="hostName"
            :user-settings-url="userSettingsUrl"
            :agent-receiver-port-is-default="agentReceiverPortIsDefault"
            @close="close"
          ></RegisterAgent>

          <CmkWizardStep v-if="isPushMode" :index="4" :is-completed="() => currentStep > 4">
            <template #header>
              <CmkHeading> {{ _t('Test connection') }}</CmkHeading>
            </template>
            <template #content>
              <CmkParagraph>
                {{
                  _t(`Test if you have configured everything correctly with pasting the following
                  command into the CLI of the target system.`)
                }}
              </CmkParagraph>
              <CmkToggleButtonGroup
                v-if="
                  currentStep === 4 && tab.statusCmdVariants && tab.statusCmdVariants.length > 1
                "
                v-model="selectedVariantId"
                class="shell-toggle"
                :options="tab.statusCmdVariants.map((v) => ({ label: v.label, value: v.id }))"
              />
              <CmkCode
                v-if="currentStep === 4"
                :code_txt="getStatusCmd(tab)"
                class="code"
                width="fill"
              />
            </template>
            <template #actions>
              <CmkWizardButton type="finish" :override-label="closeButtonTitle" @click="close" />
              <CmkWizardButton type="previous" />
            </template>
          </CmkWizardStep>
        </CmkWizard>
      </CmkTabContent>
    </template>
  </CmkTabs>
</template>

<style scoped>
/* stylelint-disable checkmk/vue-bem-naming-convention */
.select-heading {
  margin-top: var(--dimension-5);
  margin-bottom: var(--dimension-4);
}

button.close_and_test {
  gap: var(--dimension-4);
}

button.all_agents {
  gap: var(--dimension-4);
  margin-left: var(--spacing);
}

.tabs {
  display: flex;
  flex-direction: row;
  align-items: center;

  > h2 {
    margin: 0;
    padding: 0;
  }

  > .cmk-icon {
    margin-right: 16px;
  }
}

.code {
  margin: var(--dimension-5) 0 var(--dimension-7);
  width: 100%;
}

.shell-toggle {
  margin-top: var(--dimension-5);
  margin-bottom: var(--dimension-5);
}

.register-heading-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--dimension-4);
}

.save_host__div {
  margin-bottom: var(--spacing);
}

.install_url__div {
  margin-bottom: var(--spacing);
}

.download_install__content {
  width: 100%;
}

.download_install__token {
  width: fit-content;
}

.agent_slideout__paragraph_host_exists {
  display: flex;
  align-items: center;
  gap: var(--dimension-4);
}
</style>
